import type { ChartSettings } from "@/classes/BaseChart";
import { ChartDataRecord } from "@/types/charts";

export type ReportRequestBody = Record<string, unknown>;

export type LineMergeCoverage = {
  mergeKey: string;
  lineMetric: string;
  columnKeys: number;
  lineKeys: number;
  matched: number;
  unmatchedLineKeys: string[];
};

export default abstract class LineMetricMerger {
  constructor(protected readonly settings: ChartSettings) {}

  protected abstract get mergeKey(): string;

  get lineMetric(): string {
    return typeof this.settings?.lineMetric === "string" ? this.settings.lineMetric : "";
  }

  buildLineRequestBody(columnsBody: ReportRequestBody): ReportRequestBody {
    return { ...columnsBody, dimension: [this.mergeKey] };
  }

  protected rowKey(row: ChartDataRecord): string {
    return String(row[this.mergeKey] ?? "");
  }

  protected indexLineRows(lineRows: ChartDataRecord[]): Map<string, number> {
    const lineByKey = new Map<string, number>();
    const { lineMetric } = this;

    lineRows.forEach((row) => {
      const key = this.rowKey(row);
      if (!key) return;
      lineByKey.set(key, (lineByKey.get(key) ?? 0) + (Number(row[lineMetric]) || 0));
    });

    return lineByKey;
  }

  protected collectColumnKeys(columnRows: ChartDataRecord[]): Set<string> {
    const keys = new Set<string>();

    columnRows.forEach((row) => {
      const key = this.rowKey(row);
      if (key) keys.add(key);
    });

    return keys;
  }

  protected buildLineIndex(_columnRows: ChartDataRecord[], lineRows: ChartDataRecord[]): Map<string, number> {
    return this.indexLineRows(lineRows);
  }

  getCoverage(columnRows: ChartDataRecord[], lineRows: ChartDataRecord[]): LineMergeCoverage {
    const lineByKey = this.indexLineRows(lineRows);
    const columnKeys = this.collectColumnKeys(columnRows);
    const unmatchedLineKeys = [...lineByKey.keys()].filter((key) => !columnKeys.has(key));

    return {
      mergeKey: this.mergeKey,
      lineMetric: this.lineMetric,
      columnKeys: columnKeys.size,
      lineKeys: lineByKey.size,
      matched: lineByKey.size - unmatchedLineKeys.length,
      unmatchedLineKeys,
    };
  }

  merge(columnRows: ChartDataRecord[], lineRows: ChartDataRecord[]): ChartDataRecord[] {
    const { mergeKey, lineMetric } = this;

    if (!mergeKey || !lineMetric || !columnRows.length || !lineRows.length) {
      return columnRows;
    }

    const lineByKey = this.buildLineIndex(columnRows, lineRows);

    // The chart classes re-aggregate the line by the same key, so the value must land on
    // exactly one row per key - writing it on every row would multiply it by the row count.
    const claimed = new Set<string>();

    return columnRows.map((row) => {
      const key = this.rowKey(row);

      if (!key || claimed.has(key) || !lineByKey.has(key)) {
        return row;
      }

      claimed.add(key);

      return { ...row, [lineMetric]: lineByKey.get(key) as number };
    });
  }
}
